/**
 * Nowcast mini-section -- US activity teaser.
 *
 * Layout rules:
 *   - The outer chart wrapper height is PINNED at 380px so a Plotly relayout
 *     does NOT cause the surrounding `<DashboardCard>` to reflow.
 *   - `NowcastChart` from the economic-outlook feature is reused at
 *     `height={380}`. It draws NBER recession shading internally via
 *     `getRecessionShapes`, so the shading is always on here.
 */
import DashboardCard from '@/features/dashboard/components/DashboardCard';
import QueryState from '@/components/QueryState';
import NowcastChart from '@/features/economic-outlook/components/NowcastChart';
import { useNowcastSummary } from '@/features/dashboard/api';
import type { NowcastRow } from '@/features/economic-outlook/types';

/** Stable height for the chart card. */
const CHART_BOX_HEIGHT = 380;

export default function NowcastMiniSection(): JSX.Element {
  const nowcastQuery = useNowcastSummary('US', '2y');
  const nowcastRows: NowcastRow[] = nowcastQuery.data?.data ?? [];

  // QueryState only needs the state flags and the extracted rows.
  const activeQuery = {
    isLoading: nowcastQuery.isLoading,
    isError: nowcastQuery.isError,
    data: nowcastRows,
  };

  return (
    <DashboardCard
      title="Real Time Estimates of Underlying Activity"
      href="/econ-activity"
      hrefLabel="Know More"
    >
      {/*
        Pinned-height chart wrapper -- all three of `height` / `minHeight` /
        `maxHeight` are set to 380px so the box never collapses or expands
        during a Plotly relayout. Opening / closing the topic modal toggles
        `document.body { overflow: hidden }`, which shifts the page as the
        scrollbar disappears and reappears and triggers Plotly's
        `useResizeHandler`; without `maxHeight` the wrapper would grow with
        the relayout and make the `<DashboardCard>` noticeably taller.

        The wrapper has no padding/border: with `box-sizing: border-box`
        they would eat into the content box and let the inner chart's literal
        380px height burst out the bottom on every relayout. The outer
        DashboardCard already provides the white surface + shadow + border.
      */}
      <div
        className="relative w-full overflow-hidden rounded-lg bg-white"
        style={{
          height: CHART_BOX_HEIGHT,
          minHeight: CHART_BOX_HEIGHT,
          maxHeight: CHART_BOX_HEIGHT,
          maxWidth: 1100,
          margin: '0 auto 0.5rem auto',
        }}
      >
        {/* Loading / error / empty states render INSIDE the pinned wrapper
            (QueryState's views fill it) so the outer box keeps its 380px
            regardless of state. */}
        <QueryState
          query={activeQuery}
          isEmpty={() => nowcastRows.length === 0}
          loadingLabel="Loading"
          errorLabel="Failed to load nowcast data."
          emptyLabel="No nowcast data available."
        >
          {() => (
            <div className="h-full w-full">
              <NowcastChart country="US" rows={nowcastRows} height={CHART_BOX_HEIGHT} />
            </div>
          )}
        </QueryState>
      </div>
    </DashboardCard>
  );
}
